import React, { useState } from "react";
import { useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import BoxWithShadow from "../components/BoxWithShadow";
import QuoteCard from "../components/QuoteCard";
import FloatingPageNav from "../components/FloatingPageNav";
import LandingSection from "./LandingSection";
import BadgePopup from "../components/Badgepopup";

function IslamicTradePage1() {
  const [isOpen, setIsOpen] = useState(true);
  const contentRef = useRef(null);
  const titleText = "The Definition and Guidelines for Trade (Bay') ";
  const links = {
    prev: "/index",
    index: "/index",
    next: "/islamic-trade-page-2",
  };
  const questions = [
    {
      text: "1. What is the foundational requirement for a trade (Bayʿ) to be valid in Islam?",
      options: [
        {
          value: "A",
          label: "A. The price must be fixed by the government",
        },
        {
          value: "B",
          label: "B. Both parties must give mutual consent",
        },
        {
          value: "C",
          label: "C. The seller must earn a profit",
        },
        {
          value: "D",
          label: "D. The sale must involve luxury goods",
        },
      ],
      correct: "B",
    },
    {
      text: "2. Why is Riba (usury) prohibited in Islamic trade?",
      options: [
        { value: "A", label: "A. Because profit-making is not allowed" },
        {
          value: "B",
          label: "B. Because it leads to exploitation and unjust gain",
        },
        {
          value: "C",
          label: "C. Because only cash transactions are allowed in Islam",
        },
        { value: "D", label: "D. Because interest benefits both parties" },
      ],
      correct: "B",
    },
    {
      text: "3. Which of the following is NOT a condition for the validity of a sale in Islam?",
      options: [
        { value: "A", label: "A. The item must exist at the time of sale" },
        { value: "B", label: "B. The item sold must be lawful (halal)" },
        { value: "C", label: "C. The ownership must transfer permanently" },
        {
          value: "D",
          label: "D. The buyer must inspect the item with a witness present",
        },
      ],
      correct: "D",
    },
    {
      text: "4. Which ethical principle in trade is highlighted by the hadith: “The truthful and trustworthy merchant is with the Prophets, the truthful, and the martyrs”?",
      options: [
        { value: "A", label: "A. Fair pricing" },
        { value: "B", label: "B. Honesty and integrity" },
        { value: "C", label: "C. Quick delivery of goods" },
        { value: "D", label: "D. Guarantee of profit" },
      ],
      correct: "B",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);

  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={1}
        timerMinutes={60}
        currentLevel={1}
      />
      <p className="content" ref={contentRef}>
        <QuoteCard
          text="Knowledge is not merely what is memorized. Knowledge is what benefits."
          author="Imām Ash Shafi'ee"
        />
        <br />
        Welcome to <b>Day 1</b> of an enlightening journey through the
        principles of Islamic jurisprudence on finance! <br />
        <br />
        Whether you're a student, entrepreneur, professional, or simply someone
        eager to align your financial life with your faith, the content of this
        module offers you more than just information, it offers transformation.{" "}
        <br />
        Trade and commerce are not merely secular activities in Islam. They are
        spiritual endeavors infused with purpose, ethics, and immense potential
        for reward. <br />
        The Prophet Muhammad (pace and blessings be upon him) himself was a
        merchant before his prophethood, known throughout Arabia for his honesty
        and integrity. His life is a testimony that one can be deeply spiritual
        and financially successful without compromising either. <br />
        <br />
        Over the next 25 days, we will explore the foundational principles that
        govern transactions in Islam i.e concepts like <b>Bayʿ</b> (sale),{" "}
        <b>Riba</b>
        (usury), <b>Gharar</b> (uncertainty), and <b>Amānah</b> (trust). <br />
        But more than that, we’ll understand how these laws are designed not to
        restrict, but to uplift i.e to ensure justice, protect the vulnerable,
        foster mutual benefit, and bring blessings (barakah) into our economic
        lives. This course is not just for advanced students of religion, it is
        for every Muslim who buys, sells, invests, or earns a living. In other
        words, it is for all of us. In a world where the lines between ethical
        and unethical trade are increasingly blurred, Islamic commercial law
        offers clarity, direction, and spiritual grounding. <br />
        By the end of these 25 days, you will not only understand what makes a
        transaction halal or haram, rather you will appreciate the beauty of an
        economic system rooted in divine wisdom. You will come to see your
        business or profession not just as a means of income, but as an act of
        worship. Let this be your invitation to dive deep. Ask questions.
        Reflect. Engage. And most importantly, internalize the principles that
        can turn everyday trade into a means of pleasing Allah and contributing
        to a just society. May this serve as a gateway to knowledge,
        empowerment, and faith-driven action. Bismillah, let’s begin!
        <br />
        <br />
        <b>Significance of Bayʿ(trade) in Islam </b>
        <br />
        <br />
        Trade (Bayʿ) holds a central position in Islam and is considered a
        lawful means of earning livelihood. Islam not only permits buying and
        selling but also encourages it as long as it is conducted with honesty,
        transparency, and fairness. The significance of sale is evident in the
        fact that the Prophet Muhammad (peace and blessings be upon him) himself
        was a merchant before his prophethood, and he set an example of
        conducting transactions with the highest ethical standards. <br />
        <br />
        Trade plays an essential role in the economic stability of individuals
        and society, providing financial independence and fostering economic
        growth. However, Islamic teachings establish clear guidelines to ensure
        that trade remains free from deceit, exploitation, and unlawful
        practices such as usury (riba) and fraud. Any transaction that adheres
        to these principles is considered halal(permissible) and beneficial for
        both buyers and sellers. Additionally, Islam promotes ethical business
        practices that go beyond financial gain, emphasizing justice,
        cooperation, and social responsibility.
        <br />
        <br />
        Trade (Bayʿ) in Islam is not merely a transactional act but a
        manifestation of moral character, personal responsibility, and social
        trust. Islamic transactional law (fiqh al-mu‘amalat) integrates ethical
        conduct with legal validity, ensuring that business activities reflect
        the core values of faith (imān), integrity (amānah), and accountability
        (muhāsabah).
        <br />
        <br />
        <b>Definition of Trade(Bayʿ) in Islam</b> <br />
        <br />
        Trade (Bayʿ) is a fundamental concept in Islamic economic transactions,
        ensuring that buying and selling is conducted in a manner that aligns
        with Islamic ethical and legal standards. The definition of sale, as
        provided by Muslim scholars, states:
        <br />
        "Permanent exchange of goods and/or services, whether immediate or
        deferred, without inclusion of usury or debt, executed with affirmation
        of the seller followed by acceptance from the buyer." <br />
        This definition highlights several key aspects that differentiate a
        lawful sale in Islam from other types of financial transactions.
        <br />
        <br />
        <BoxWithShadow>
          <strong>IMPORTANT</strong>
          <br />
          The classical jurists (fuqahā) have further classified different types
          of Bayʿ, such as Bayʿ as-Salam (advance payment sale), and Bayʿ
          al-Wikalah’ (sale via deputy), all subject to conditions of fairness,
          clarity, and absence of ambiguity (gharar). We will understand many of
          them in the coming days In Shaa Allah.
          <br />
        </BoxWithShadow>
        <br /> Islamic jurisprudence lays down essential guidelines that must be
        fulfilled for a trade to be considered permissible. These include:
        <br />
        <br />
        <b> </b> <br />
      </p>
      <table className="trade-table">
        <tr>
          <th>Key Guidelines for a Trade to Be Lawful in Islam</th>
        </tr>
        <tr>
          <td>
            The Sale should occur with mutual consent of the buyer and the
            seller
          </td>
        </tr>
        <tr>
          <td>The Sale should be free from Riba(Usury) and Ghish(Fraud)</td>
        </tr>
        <tr>
          <td>The good or service being sold must exist</td>
        </tr>
        <tr>
          <td>
            The good or service being sold must be lawful to possess and benefit
            from
          </td>
        </tr>
        <tr>
          <td>The Sale should end with an absolute transfer of ownership</td>
        </tr>
        <tr>
          <td>The Sale should be conducted ethically</td>
        </tr>
      </table>
      <p className="content">
        {" "}
        <br />
        <br /> <b>1. Mutual Consent and Transparency</b>
        <br />
        <br /> Sale in Islam is only valid when both parties willingly agree to
        the terms of the transaction without coercion or deception. In Islamic
        commercial transactions, one of the most fundamental conditions is that
        both parties enter into the agreement with full mutual consent and
        transparency. This principle is deeply rooted in the Qur’an, where Allah
        says: <br />
        "O you who have believed, do not consume one another’s wealth unjustly
        but only [do so] in sale by mutual consent." (
        <a
          href="https://quran.com/4:29/tafsirs/en-tafisr-ibn-kathir"
          target="blank"
        >
          Surah An-Nisa, 4:29
        </a>
        )
        <br />
        The verse clearly emphasizes that consent is not just a formal
        requirement but a moral and ethical foundation of trade.
        <br />
        The Prophet (peace and blessings be upon him) also reinforced the
        importance of honesty and openness in sale:
        <br /> "The buyer and the seller have the option of canceling the
        contract unless they separate, and if they are truthful and clear in the
        transaction, they will be blessed in their transaction, but if they
        conceal anything or lie, the blessing of their transaction will be
        erased."{" "}
        <a href="https://sunnah.com/bukhari:2079" target="blank">
          (Sahih al-Bukhari 2079)
        </a>
        <br />
        Mutual consent implies that neither the buyer nor the seller is forced,
        deceived, or manipulated into the transaction. Both parties must
        willingly and knowingly agree to the terms of exchange. If one party is
        coerced, pressured, or misled, the transaction becomes invalid in the
        eyes of Shariah. For instance, if a seller hides defects of a product or
        exaggerates its qualities to lure the buyer, the consent of the buyer is
        no longer genuine, as it is based on misinformation. Transparency,
        therefore, becomes inseparable from consent.
        <br />
        The wisdom behind this condition is that trade in Islam is not merely
        about transferring goods for money but about ensuring justice, fairness,
        and goodwill. If transactions were allowed without true consent,
        powerful individuals could exploit the weak, the uninformed could be
        taken advantage of, and trust in markets would collapse. By insisting on
        transparency, Islam creates an environment where buyers and sellers feel
        safe and secure in their dealings. This builds long-term relationships,
        strengthens communities, and reduces disputes.
        <br />
        Practically, this condition manifests in the requirement of bay’
        al-‘inah (clear offer and acceptance). In classical fiqh, scholars
        emphasized that trade must involve an offer (ijab) and acceptance
        (qabul) stated clearly, leaving no room for ambiguity. Even today,
        contracts in Islamic finance follow the same rule, requiring explicit
        agreement so both parties know exactly what they are committing to.
        <br />
        The Prophet Muhammad (peace be upon him) also taught that both the buyer
        and the seller have the right to cancel a deal as long as they have not
        parted ways. This protects both sides from hasty decisions and ensures
        satisfaction. Moreover, Islam encourages traders to be honest about the
        quality, price, and delivery terms of their goods. A hadith in Sahih
        Muslim warns: “He who deceives us is not one of us.” This strong
        statement underlines how dishonesty undermines mutual consent and
        invalidates the ethical spirit of trade.
        <br />
        The wisdom of this rule extends to modern economic contexts as well. In
        an age where hidden terms, fine print, and manipulative marketing are
        common, Islam’s insistence on full disclosure remains highly relevant.
        True consent cannot exist where information is deliberately withheld or
        obscured. Hence, transparency protects individuals from exploitation and
        promotes fairness in society.
        <br />
        Ultimately, mutual consent and transparency align commerce with Islamic
        values of justice, dignity, and respect. By safeguarding the will and
        knowledge of both parties, Islam prevents oppression and ensures that
        wealth circulates through fair means, preserving harmony in social and
        economic life.
        <br />
        Thus, both parties must disclose all relevant details about the item or
        service being sold, avoiding misinformation or deception. Transparency
        also extends to fair pricing, accurate descriptions, clear terms of
        exchange, and acknowledging the right of return in the case of valid
        complaints. In a broader sense, transparency cultivates a climate of
        trust and strengthens social cohesion. <br />
        <br />
        <b>2. Prohibition of Riba (Usury) and Fraud</b>
        <br />
        <br /> Islam strictly prohibits Riba (interest or usury), as it leads to
        exploitation and economic disparity. The prohibition of riba (usury) and
        fraud is one of the most distinctive features of Islamic trade
        regulations. The Qur’an categorically condemns riba in multiple verses,
        declaring:
        <br /> "Allah has permitted sale and has forbidden usury."{" "}
        <a
          href="https://quran.com/2:275/tafsirs/en-tafisr-ibn-kathir"
          target="blank"
        >
          (Surah Al-Baqarah, 2:275)
        </a>
        <br />
        Fraud, deception, and unjust enrichment are likewise strongly condemned
        in hadith literature. The combination of these prohibitions ensures that
        trade remains fair, ethical, and beneficial for all parties.
        <br />
        Riba refers to any guaranteed, predetermined increase on loans or
        exchanges of similar commodities without adding real value. In trade,
        riba can manifest in multiple forms: charging interest on delayed
        payments, exchanging unequal quantities of the same commodity, or
        manipulating contracts to secure an unjust gain. Islam prohibits riba
        because it creates exploitation and injustice. One party benefits
        without bearing any risk, while the other is burdened with guaranteed
        loss. In contrast, trade is meant to involve risk-sharing and mutual
        benefit.
        <br />
        The wisdom behind prohibiting riba and fraud is multifaceted. First, it
        prevents economic exploitation. Interest-based transactions tend to
        enrich lenders at the expense of borrowers, leading to wealth
        concentration and poverty cycles. Fraudulent practices create mistrust
        and insecurity, destabilizing markets. By eliminating these, Islam
        ensures that wealth circulates fairly and that the economic system
        serves society rather than oppresses it.
        <br />
        Second, the prohibition nurtures moral responsibility. When traders
        cannot rely on guaranteed interest or dishonest practices, they must
        engage in genuine, value-adding activities such as production,
        investment, and service delivery. This creates real economic growth and
        social development. It also cultivates honesty, integrity, and
        accountability, values that benefit not only the marketplace but the
        entire community.
        <br />
        Also, these prohibitions protect social harmony. Riba often leads to
        resentment, social inequality, and economic instability. Fraud erodes
        trust and creates disputes. By outlawing both, Islam preserves peace and
        cooperation among people. Modern relevance of these prohibitions is
        striking. Today’s global financial crises have often been traced back to
        excessive reliance on interest-based debt, speculation, and fraudulent
        practices like misreporting or concealing risks. The Islamic prohibition
        of riba and fraud, therefore, provides a timeless framework for building
        stable and ethical economic systems.
        <br /> In addition to riba, all forms of fraudulent activities, such as
        price manipulation, selling defective goods without disclosure, and
        engaging in monopolistic practices, are forbidden in sale.
        <br /> The Prophet (peace and blessings be upon him) warned against
        fraudulent transactions: <br />
        "Whoever deceives is not from us."{" "}
        <a href="https://sunnah.com/muslim:102" target="blank">
          (Sahih Muslim 102)
        </a>
        <br />
        <br />
        Furthermore, deceptive business strategies, such as artificial price
        inflation or creating false scarcity to drive up demand, are considered
        unethical and harmful to society. Islam also condemns the hoarding of
        essential goods to create artificial demand, as this leads to market
        imbalance and social injustice. <br />
        In short, Islam permits profit from trade but only when it is earned
        fairly, transparently, and through real value creation. By prohibiting
        riba and fraud, it removes exploitation and ensures that commerce
        uplifts society instead of harming it.
        <br />
        <br />
        <b>3. The Existence of the Sold Item</b>
        <br />
        <br />
        Another essential condition for a valid trade in Islam is that the item
        being sold must exist at the time of the transaction. Selling something
        that does not exist, or that the seller does not yet own, is prohibited
        under Shariah because it introduces excessive uncertainty (gharar) and
        unfair risk.
        <br />
        Classical Islamic jurisprudence draws this rule from the hadith where
        the Prophet (peace be upon him) forbade the sale of “what is not with
        you” (Abu Dawood). Similarly, the sale of unborn animals, unripe fruits,
        or fish not yet caught was prohibited unless certain conditions removed
        the uncertainty. The wisdom is clear: both parties must have certainty
        about what is being exchanged. If the item does not exist, the buyer
        risks paying for nothing, and the seller benefits without delivering.
        <br />
        The requirement of existence ensures fairness and prevents disputes.
        When the item is present, both parties can inspect it, verify its
        condition, and agree on a fair price. This removes ambiguity and ensures
        mutual satisfaction. If items were allowed to be sold before existence,
        disputes would multiply, trust would erode, and markets would become
        chaotic.
        <br />
        This principle also highlights Islam’s practical and ethical approach to
        commerce. It recognizes that speculation in nonexistent goods often
        benefits a few at the expense of many. By requiring actual existence,
        Islam ties trade to real economic activity. It ensures that wealth is
        exchanged for tangible value, not for mere promises or empty contracts.
        <br />
        The wisdom is evident in modern contexts as well. Many financial crises
        have stemmed from trading nonexistent or imaginary assets or speculative
        bubbles. Islam’s insistence on existence anchors trade in reality and
        prevents exploitation through empty speculation. However, exceptions
        exist, such as the Salam contract, where advance payment is made for
        goods that will be delivered at a later date under strict conditions.{" "}
        <br />
        Another form, known as Istisna'a, allows for the sale of manufactured
        goods that are yet to be produced, ensuring that production is carried
        out according to specific agreed-upon conditions. These exceptions
        reflect the flexibility of Islamic commercial law to accommodate
        legitimate business needs while upholding ethical standards.We will
        understand these exceptions in the subsequent sections <br />
        <br />
        <b>4. Lawfulness of the Sold Item</b> <br />
        <br />
        Islam also requires that the item being sold must be lawful (halal) and
        permissible under Shariah. This principle ensures that commerce aligns
        with the ethical and spiritual framework of Islam. Selling prohibited
        items such as alcohol, pork, idols, or anything harmful to society is
        strictly forbidden, even if the transaction appears profitable.
        <br />
        A Trade is only permissible in goods and services that are lawful
        (halal) in Islam.
        <br />
        The Qur’an declares: “They ask you what is lawful for them. Say: Lawful
        unto you are all good things”{" "}
        <a href="https://quran.com/5/4">(Surah Al-Ma’idah 5:4)</a> <br />
        Similarly, the Prophet (peace be upon him) said: “When Allah forbids a
        thing, He also forbids its price” (Abu Dawood). This means that Muslims
        cannot profit from selling what Allah has prohibited, since earning from
        such sales is itself unlawful.
        <br />
        The wisdom behind this condition is clear: Islam views trade not just as
        an economic activity but as a moral responsibility. A Muslim’s wealth
        must be pure, and engaging in the sale of unlawful items corrupts both
        the soul and society. For instance, alcohol may generate profit, but it
        causes social harm through addiction, crime, and family breakdown. Islam
        prioritizes long-term societal well-being over short-term financial
        gain.
        <br />
        This principle also ensures that trade supports human dignity and
        health. By prohibiting harmful goods, Islam protects people from
        exploitation and harm. It ensures that markets become sources of
        benefit, not destruction. This also prevents wealth from circulating
        through immoral channels, which can lead to social corruption and
        injustice.
        <br />
        Additionally, scholars differentiate between permissible and
        impermissible cases of gifting or selling certain items. <br />
        For example, a Muslim may donate or sell a hunting or guard dog, as it
        serves a permissible purpose. However, gifting or selling a dog purely
        for companionship (as a pet) is not allowed, as the Prophet (peace and
        blessings be upon him) prohibited keeping dogs without necessity. <br />
        Islam also places importance on ethical sourcing, ensuring that goods
        sold are obtained through legitimate and fair means. Selling stolen,
        counterfeit, or misrepresented products is considered unlawful.
        Furthermore, Islam promotes environmentally and socially responsible
        trade practices that safeguard human dignity and protect creation.
        <br />
        Ultimately, this condition shows that commerce in Islam is not
        value-neutral. It is guided by moral and spiritual considerations. By
        ensuring that only lawful items are traded, Islam preserves the sanctity
        of wealth, aligns business with divine guidance, and safeguards the
        well-being of individuals and society.
        <br />
        <br />
        <b>5. Permanent Exchange and Transfer of Ownership </b>
        <br />
        <br />A valid trade in Islam requires that the transaction results in a
        permanent exchange and transfer of ownership. Temporary arrangements or
        unclear transfers are not sufficient. This ensures clarity, finality,
        and fairness in transactions.
        <br />
        Trade in Islamic jurisprudence refers to a permanent exchange of goods
        or services. Temporary exchanges, such as rentals or leasing, fall under
        different legal categories and are not considered sale (Bayʿ) in the
        strict sense. The definition of sale includes the affirmation of the
        seller, followed by acceptance from the buyer, which finalizes the
        transaction. Once possession is taken, the sale is completed and
        binding.
        <br />
        Once a sale is completed with offer and acceptance, the ownership of the
        item passes to the buyer and the payment becomes the property of the
        seller. Neither party should remain in doubt about who owns what. This
        rule eliminates ambiguity and potential disputes.
        <br />
        The wisdom behind this condition is that trade is meant to create real
        and stable changes in ownership, not to encourage uncertainty or
        exploitation. If ownership is not permanently transferred, both parties
        remain vulnerable to disputes. For example, if a seller retains hidden
        claims over a sold item, the buyer’s use of it could later be
        challenged, creating injustice and instability.
        <br />
        Islam also prohibits sales that disguise loans or interest-bearing
        contracts under the guise of trade. For example, a sale where the seller
        retains ownership while merely collecting returns from the buyer may
        turn into riba in disguise. The requirement of permanent transfer
        prevents such exploitation.
        <br />
        This principle also ensures accountability. Once ownership is
        transferred, the buyer becomes responsible for the item, including its
        risks and benefits. This aligns with the Islamic principle that
        liability goes hand in hand with ownership. A person cannot enjoy the
        profits of an asset without also bearing its risks. This creates
        fairness and balance in economic dealings.
        <br />
        Additionally, Islam encourages ensuring that documentation, receipts, or
        witnesses are involved in significant transactions to avoid disputes and
        protect both parties' rights. <br />
        In cases involving large sums or complex conditions, written contracts
        (kitābah) are strongly recommended, in line with the Qur’anic
        instruction in{" "}
        <a
          href="https://quran.com/2:282/tafsirs/en-tafisr-ibn-kathir"
          target="blank"
        >
          Surah Al-Baqarah (2:282)
        </a>
        , the longest verse of the Qur'an, which emphasizes recording debts and
        commercial agreements.
        <br />
        Ultimately, this condition ensures that trade achieves its real purpose:
        the fair and permanent exchange of goods and wealth. It eliminates
        ambiguity, prevents disguised riba, and promotes stability and justice
        in economic life.
        <br />
        <br />
        <b>6. The Ethical and Moral Aspect of Sale in Islam</b> <br />
        <br />
        Sale is not just about buying and selling; it carries ethical and moral
        responsibilities. Islam encourages sellers to conduct business with
        sincerity and fairness, ensuring that neither party is harmed. Some key
        ethical principles include honesty in pricing and weight measurement,
        avoiding hoarding and exploitation, fulfilling promises and contracts,
        and showing good character and conduct.
        <br />
        The Prophet (peace and blessings be upon him) said: <br />
        "The truthful and trustworthy merchant is with the Prophets, the
        truthful, and the martyrs."{" "}
        <a href="https://sunnah.com/tirmidhi:1209" target="blank">
          (Jami' At-Tirmidhi 1209)
        </a>
        <br />
        This hadith highlights the immense reward for sellers who uphold honesty
        and integrity in their business dealings. Islamic history is replete
        with examples of pious traders whose moral excellence and fair dealing
        were instrumental in spreading Islam to new regions.
        <br />
        Moreover, Islamic trade ethics emphasize social responsibility, ensuring
        that business practices uplift the poor, discourage materialism, and
        serve the greater good. Generosity, community support, and ethical
        consumption are interwoven into the Islamic economic framework, making
        trade a path to spiritual elevation, not just material success.
        <br />A trade (Bayʿ) is an essential part of Islamic economic life and
        is fully permissible when conducted within the boundaries set by the
        Qur'an and Sunnah. Islam encourages sale as a means of lawful earnings
        while emphasizing fairness, transparency, and the avoidance of unethical
        practices. As long as sale is conducted with mutual consent, free from
        riba and deceit, and involves lawful goods and services, it remains a
        blessed and rewarding act in the sight of Allah.
        <br />
        By adhering to these principles, Muslim sellers can not only prosper
        financially but also contribute to a just and ethical economic system
        that benefits society as a whole. Furthermore, ethical trade fosters
        trust, strengthens communities, and ensures that business dealings
        remain a source of goodwill and blessings (barakah). Ultimately, the
        Islamic approach to trade serves as a holistic model that balances
        personal benefit with communal welfare and spiritual integrity.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        Today, we embarked on our journey into the world of Islamic Trade
        (Bayʿ), understanding its vital role in both personal livelihood and the
        broader economy. We learned that trade in Islam is more than a financial
        activity it is a reflection of character, faith, and responsibility. The
        Prophet Muhammad (peace and blessings be upon him) himself was a model
        of ethical commerce, and Islamic jurisprudence continues to uphold high
        standards of integrity, transparency, and justice in every transaction.
        <br />
        <br />
        We covered the foundational guidelines that make a trade lawful in
        Islam, including:
        <ul>
          <li>Mutual consent and transparency in all transactions</li>
          <li>
            Prohibition of riba (usury) and fraud, to ensure fairness and
            prevent exploitation
          </li>
          <li>
            The requirement for the sold item to exist and be lawful (halal)
          </li>
          <li>
            The importance of clear ownership transfer and ethical business
            conduct
          </li>
        </ul>
        <br />
        We also touched on the spiritual dimension of trade, where honesty and
        trustworthiness in commerce elevate a person in the sight of Allah and
        benefit society as a whole.
        <br />
        <br />
        <b> Coming Up Next</b>
        <br />
        <br />
        Now that we have explored the essence and guidelines of trade in Islam,
        tomorrow we will delve deeper into the specific conditions of a valid
        trade. We will examine the criteria that scholars have laid out to
        ensure that every transaction aligns with the objectives of Shariah i.e
        protecting wealth, preserving justice, and promoting ethical conduct.
        Stay curious and engaged this journey is just beginning!
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />

      <BadgePopup
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        level="Level 0"
        badgeImg="/achieve.png"
        heading="Fiqh Flamebearer"
        subtitle="Beginner - Islamic Jurisprudence on Finance"
        mainImg="./fiqhflame.png"
        description="Awarded for beginning your study into Islamic Financial Jurisprudence, recognizing your pure intention (Niyyah) to seek lawful wealth."
      />
    </div>
  );
}

export default IslamicTradePage1;
